import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import DashboardLayout from '../components/common/DashboardLayout';
import { DashboardSkeleton } from '../components/common/Skeleton';

// Lazy-load role dashboards for code splitting
const LazyDeliveryDashboard = lazy(() => import('../pages/roles/DeliveryDashboard'));
const LazySupportDashboard = lazy(() => import('../pages/roles/SupportDashboard'));
const LazyAffiliateDashboard = lazy(() => import('../pages/roles/AffiliateDashboard'));
const LazyWarehouseDashboard = lazy(() => import('../pages/roles/WarehouseDashboard'));
const LazyModeratorDashboard = lazy(() => import('../pages/roles/ModeratorDashboard'));
const LazyFinanceDashboard = lazy(() => import('../pages/roles/FinanceDashboard'));
const LazySuperAdminDashboard = lazy(() => import('../pages/roles/SuperAdminDashboard'));
const LazyReturnsManagerDashboard = lazy(() => import('../pages/roles/ReturnsManagerDashboard'));
const LazyMarketingDashboard = lazy(() => import('../pages/roles/MarketingDashboard'));
const LazyCatalogManagerDashboard = lazy(() => import('../pages/roles/CatalogManagerDashboard'));
const LazyFarmerOnboardingDashboard = lazy(() => import('../pages/roles/FarmerOnboardingDashboard'));
const LazyAdManagerDashboard = lazy(() => import('../pages/roles/AdManagerDashboard'));
const LazyApiPartnerDashboard = lazy(() => import('../pages/roles/ApiPartnerDashboard'));
const LazyCommunityManagerDashboard = lazy(() => import('../pages/roles/CommunityManagerDashboard'));
const LazyDisputeArbitratorDashboard = lazy(() => import('../pages/roles/DisputeArbitratorDashboard'));
const LazyQATesterDashboard = lazy(() => import('../pages/roles/QATesterDashboard'));
const LazyRegionalManagerDashboard = lazy(() => import('../pages/roles/RegionalManagerDashboard'));
const LazyRoleReferencePage = lazy(() => import('../pages/roles/RoleReferencePage'));
const LazyFeatureTracker = lazy(() => import('../pages/admin/PlatformFeatureTracker'));

const LazyWrap = ({ children }) => (
  <Suspense fallback={<DashboardLayout><DashboardSkeleton /></DashboardLayout>}>
    {children}
  </Suspense>
);

// Pages
import HomePage from '../pages/HomePage';
import { TermsPage, PrivacyPage, ContactPage, HelpPage, AboutPage, FAQPage } from '../pages/static/StaticPages';
import LoginPage from '../pages/auth/LoginPage';
import RegisterPage from '../pages/auth/RegisterPage';
import ChooseRolePage from '../pages/auth/ChooseRolePage';
import ChooseRoleSignupPage from '../pages/auth/ChooseRoleSignupPage';
import MerchantLoginPage from '../pages/auth/FarmerLoginPage';
import AdminLoginPage from '../pages/auth/AdminLoginPage';
import PortalRegisterPage from '../pages/auth/PortalRegisterPage';
import ForgotPasswordPage from '../pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '../pages/auth/ResetPasswordPage';
import VerifyEmailPage from '../pages/auth/VerifyEmailPage';
import ProductsPage from '../pages/customer/ProductsPage';
import CategoriesPage from '../pages/customer/CategoriesPage';
import ProductDetailPage from '../pages/customer/ProductDetailPage';
import StoresPage from '../pages/customer/StoresPage';
import StoreDetailPage from '../pages/customer/StoreDetailPage';
import CartPage from '../pages/customer/CartPage';
import CheckoutPage from '../pages/customer/CheckoutPage';
import PaymentPage from '../pages/customer/PaymentPage';
import OrderHistoryPage from '../pages/customer/OrderHistoryPage';
import OrderDetailPage from '../pages/customer/OrderDetailPage';
import CompareProducts from '../pages/customer/CompareProducts';
import ProfilePage from '../pages/customer/ProfilePage';
import WishlistPage from '../pages/customer/WishlistPage';
import WalletPage from '../pages/customer/WalletPage';
import AccountSecurityPage from '../pages/customer/AccountSecurityPage';
import FlashDealsPage from '../pages/customer/FlashDealsPage';
import GiftCardsPage from '../pages/customer/GiftCardsPage';
import NotificationPrefsPage from '../pages/customer/NotificationPrefsPage';
import FarmerDashboard from '../pages/merchant/FarmerDashboard';
import FarmerProductsPage from '../pages/merchant/FarmerProductsPage';
import FarmerProductFormPage from '../pages/merchant/FarmerProductFormPage';
import FarmerOrdersPage from '../pages/merchant/FarmerOrdersPage';
import FarmerStoreSettingsPage from '../pages/merchant/FarmerStoreSettingsPage';
import FarmerVerificationPage from '../pages/merchant/FarmerVerificationPage';
import FarmerCouponsPage from '../pages/merchant/FarmerCouponsPage';
import FarmerBulkImportPage from '../pages/merchant/FarmerBulkImportPage';
import AdminDashboard from '../pages/admin/AdminDashboard';
import AdminUsersPage from '../pages/admin/AdminUsersPage';
import AdminStoresPage from '../pages/admin/AdminStoresPage';
import AdminOrdersPage from '../pages/admin/AdminOrdersPage';
import AdminRevenuePage from '../pages/admin/AdminRevenuePage';

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<ChooseRoleSignupPage />} />
      <Route path="/register/old" element={<RegisterPage />} />
      <Route path="/choose-role" element={<ChooseRolePage />} />
      <Route path="/farmer/login" element={<MerchantLoginPage />} />
      <Route path="/admin/:roleSlug" element={<AdminLoginPage />} />
      {/* NOTE: This route uses _login suffix (e.g. /admin/admin_login, /admin/support_login) 
          so it won't conflict with /admin/dashboard, /admin/users etc. which are defined below */}
      <Route path="/register/:role" element={<PortalRegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
      <Route path="/products" element={<ProductsPage />} />
      <Route path="/products/:slug" element={<ProductDetailPage />} />
      <Route path="/stores" element={<StoresPage />} />
      <Route path="/compare" element={<CompareProducts />} />
      <Route path="/stores/:slug" element={<StoreDetailPage />} />
      <Route path="/cart" element={<CartPage />} />

      {/* Customer Routes (Protected) */}
      <Route
        path="/checkout"
        element={
          <ProtectedRoute roles={[]}>
            <CheckoutPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/checkout/payment"
        element={
          <ProtectedRoute roles={[]}>
            <PaymentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/orders"
        element={
          <ProtectedRoute roles={[]}>
            <OrderHistoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/orders/:id"
        element={
          <ProtectedRoute roles={[]}>
            <OrderDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/profile"
        element={
          <ProtectedRoute roles={[]}>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route path="/flash-deals" element={<FlashDealsPage />} />
      <Route path="/flash-deals/:id" element={<FlashDealsPage />} />
      <Route path="/gift-cards" element={<GiftCardsPage />} />
      <Route
        path="/account/wishlist"
        element={
          <ProtectedRoute roles={[]}>
            <WishlistPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/wallet"
        element={
          <ProtectedRoute roles={[]}>
            <WalletPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/security"
        element={
          <ProtectedRoute roles={[]}>
            <AccountSecurityPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/notifications"
        element={
          <ProtectedRoute roles={[]}>
            <NotificationPrefsPage />
          </ProtectedRoute>
        }
      />

      {/* Merchant Routes (with sidebar layout) */}
      <Route
        path="/farmer/dashboard"
        element={
          <ProtectedRoute roles={['farmer', 'farmer_staff', 'admin', 'super_admin']}>
            <DashboardLayout><FarmerDashboard /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/farmer/products"
        element={
          <ProtectedRoute roles={['farmer', 'farmer_staff', 'admin', 'super_admin']}>
            <DashboardLayout><FarmerProductsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/farmer/products/new"
        element={
          <ProtectedRoute roles={['farmer', 'farmer_staff', 'admin', 'super_admin']}>
            <DashboardLayout><FarmerProductFormPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/farmer/products/:id/edit"
        element={
          <ProtectedRoute roles={['farmer', 'farmer_staff', 'admin', 'super_admin']}>
            <DashboardLayout><FarmerProductFormPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/farmer/orders"
        element={
          <ProtectedRoute roles={['farmer', 'farmer_staff', 'admin', 'super_admin']}>
            <DashboardLayout><FarmerOrdersPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/farmer/farm-settings"
        element={
          <ProtectedRoute roles={['farmer', 'farmer_staff', 'admin', 'super_admin']}>
            <DashboardLayout><FarmerStoreSettingsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/farmer/verification"
        element={
          <ProtectedRoute roles={['farmer', 'farmer_staff']}>
            <DashboardLayout><FarmerVerificationPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/farmer/coupons"
        element={
          <ProtectedRoute roles={['farmer', 'farmer_staff', 'admin', 'super_admin']}>
            <DashboardLayout><FarmerCouponsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/farmer/bulk-import"
        element={
          <ProtectedRoute roles={['farmer', 'farmer_staff', 'admin', 'super_admin']}>
            <DashboardLayout><FarmerBulkImportPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Admin Routes (with sidebar layout) */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute roles={['admin', 'super_admin']}>
            <DashboardLayout><AdminDashboard /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute roles={['admin', 'super_admin']}>
            <DashboardLayout><AdminUsersPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/stores"
        element={
          <ProtectedRoute roles={['admin', 'super_admin']}>
            <DashboardLayout><AdminStoresPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/orders"
        element={
          <ProtectedRoute roles={['admin', 'super_admin']}>
            <DashboardLayout><AdminOrdersPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/revenue"
        element={
          <ProtectedRoute roles={['admin', 'super_admin']}>
            <DashboardLayout><AdminRevenuePage /></DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Static Pages */}
      <Route path="/terms" element={<TermsPage />} />

      {/* Role-Based Dashboards */}
      <Route
        path="/delivery/dashboard"
        element={
          <ProtectedRoute roles={['delivery', 'admin', 'super_admin']}>
            <LazyWrap><DashboardLayout><LazyDeliveryDashboard /></DashboardLayout></LazyWrap>
          </ProtectedRoute>
        }
      />
      <Route
        path="/support/dashboard"
        element={
          <ProtectedRoute roles={['support', 'admin', 'super_admin']}>
            <LazyWrap><DashboardLayout><LazySupportDashboard /></DashboardLayout></LazyWrap>
          </ProtectedRoute>
        }
      />
      <Route
        path="/affiliate/dashboard"
        element={
          <ProtectedRoute roles={['affiliate', 'admin', 'super_admin']}>
            <LazyWrap><DashboardLayout><LazyAffiliateDashboard /></DashboardLayout></LazyWrap>
          </ProtectedRoute>
        }
      />
      <Route
        path="/warehouse/dashboard"
        element={
          <ProtectedRoute roles={['warehouse', 'admin', 'super_admin']}>
            <LazyWrap><DashboardLayout><LazyWarehouseDashboard /></DashboardLayout></LazyWrap>
          </ProtectedRoute>
        }
      />
      <Route
        path="/moderator/dashboard"
        element={
          <ProtectedRoute roles={['moderator', 'admin', 'super_admin']}>
            <LazyWrap><DashboardLayout><LazyModeratorDashboard /></DashboardLayout></LazyWrap>
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance/dashboard"
        element={
          <ProtectedRoute roles={['finance', 'admin', 'super_admin']}>
            <LazyWrap><DashboardLayout><LazyFinanceDashboard /></DashboardLayout></LazyWrap>
          </ProtectedRoute>
        }
      />
      <Route
        path="/super-admin/dashboard"
        element={
          <ProtectedRoute roles={['super_admin']}>
            <LazyWrap><DashboardLayout><LazySuperAdminDashboard /></DashboardLayout></LazyWrap>
          </ProtectedRoute>
        }
      />
      <Route
        path="/super-admin/role-reference"
        element={
          <ProtectedRoute roles={['super_admin']}>
            <LazyWrap><DashboardLayout><LazyRoleReferencePage /></DashboardLayout></LazyWrap>
          </ProtectedRoute>
        }
      />
      <Route
        path="/super-admin/feature-tracker"
        element={
          <ProtectedRoute roles={['super_admin']}>
            <LazyWrap><DashboardLayout><LazyFeatureTracker /></DashboardLayout></LazyWrap>
          </ProtectedRoute>
        }
      />
      <Route path="/returns-manager/dashboard" element={
        <ProtectedRoute roles={['returns_manager', 'admin', 'super_admin']}>
          <LazyWrap><DashboardLayout><LazyReturnsManagerDashboard /></DashboardLayout></LazyWrap>
        </ProtectedRoute>
      } />
      <Route path="/marketing/dashboard" element={
        <ProtectedRoute roles={['marketing', 'admin', 'super_admin']}>
          <LazyWrap><DashboardLayout><LazyMarketingDashboard /></DashboardLayout></LazyWrap>
        </ProtectedRoute>
      } />
      <Route path="/catalog/dashboard" element={
        <ProtectedRoute roles={['catalog_manager', 'admin', 'super_admin']}>
          <LazyWrap><DashboardLayout><LazyCatalogManagerDashboard /></DashboardLayout></LazyWrap>
        </ProtectedRoute>
      } />
      <Route path="/farmer-onboarding/dashboard" element={
        <ProtectedRoute roles={['farmer_onboarding', 'admin', 'super_admin']}>
          <LazyWrap><DashboardLayout><LazyFarmerOnboardingDashboard /></DashboardLayout></LazyWrap>
        </ProtectedRoute>
      } />
      <Route path="/ad-manager/dashboard" element={
        <ProtectedRoute roles={['ad_manager', 'admin', 'super_admin']}>
          <LazyWrap><DashboardLayout><LazyAdManagerDashboard /></DashboardLayout></LazyWrap>
        </ProtectedRoute>
      } />
      <Route path="/api-partner/dashboard" element={
        <ProtectedRoute roles={['api_partner', 'admin', 'super_admin']}>
          <LazyWrap><DashboardLayout><LazyApiPartnerDashboard /></DashboardLayout></LazyWrap>
        </ProtectedRoute>
      } />
      <Route path="/community/dashboard" element={
        <ProtectedRoute roles={['community_manager', 'admin', 'super_admin']}>
          <LazyWrap><DashboardLayout><LazyCommunityManagerDashboard /></DashboardLayout></LazyWrap>
        </ProtectedRoute>
      } />
      <Route path="/disputes/dashboard" element={
        <ProtectedRoute roles={['dispute_arbitrator', 'admin', 'super_admin']}>
          <LazyWrap><DashboardLayout><LazyDisputeArbitratorDashboard /></DashboardLayout></LazyWrap>
        </ProtectedRoute>
      } />
      <Route path="/qa-tester/dashboard" element={
        <ProtectedRoute roles={['qa_tester', 'admin', 'super_admin']}>
          <LazyWrap><DashboardLayout><LazyQATesterDashboard /></DashboardLayout></LazyWrap>
        </ProtectedRoute>
      } />
      <Route path="/regional/dashboard" element={
        <ProtectedRoute roles={['regional_manager', 'admin', 'super_admin']}>
          <LazyWrap><DashboardLayout><LazyRegionalManagerDashboard /></DashboardLayout></LazyWrap>
        </ProtectedRoute>
      } />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/categories" element={<CategoriesPage />} />
      <Route path="/categories/:slug" element={<CategoriesPage />} />

      {/* 404 */}
      <Route path="*" element={
        <div className="text-center section-padding">
          <div style={{ fontSize: '5rem', marginBottom: '1rem' }}>🔍</div>
          <h1 className="display-4 fw-bold" style={{ color: '#B91C1C' }}>404</h1>
          <p className="text-muted fs-5 mb-4">The page you're looking for doesn't exist.</p>
          <a href="/" className="btn btn-fd-primary btn-lg me-2">Go Home</a>
          <a href="/products" className="btn btn-fd-outline btn-lg">Browse Products</a>
        </div>
      } />
    </Routes>
  );
};

export default AppRoutes;
